// offline/deliveryState.js — LA TABLA DE ESTADOS de la cara confiable (F6.7b).
//
// Funciones puras, sin imports, sin efectos. Aquí vive TODA la decisión de
// "qué debe ver el usuario"; las pantallas solo pintan lo que estas funciones
// responden. Se puede correr mil veces con las mismas entradas y da lo mismo.
//
// LAS DOS VERDADES, que NO son la misma y por eso no se mezclan:
//
//   PANEL = verdad del SERVIDOR. Lo que el servidor dice que existe en la
//     cuenta. Sin señal, la última verdad de servidor conocida (la foto local
//     de la lista). Un doc que el servidor dice `processing` se pinta
//     Processing aunque el teléfono no tenga nada suyo: es cierto.
//
//   CENTRO DE ESTUDIO = verdad del TELÉFONO. Lo que de verdad se puede abrir
//     ahora mismo. Un doc que el servidor tiene listo pero que no bajó no se
//     puede estudiar, y prometerlo sería mentir.
//
// ── #412 (LISTO-LOCAL B3) — Y AHORA EL PANEL TAMPOCO DICE «LISTO» ANTES DE
//    TIEMPO. ESTA CABECERA CAMBIÓ CON LA FIRMA, Y SE REESCRIBE ENTERA ─────────
//
// Lo de arriba seguía siendo cierto como descripción de DÓNDE vive cada verdad,
// y era falso como regla de lo que el usuario debe leer. El renglón que decía
// «un doc que el servidor dice X se pinta X aunque el teléfono no tenga nada
// suyo: es cierto» cubría también el caso caro: el servidor dice `ready`, la
// tarjeta dice «Listo», el usuario la toca y el Centro de Estudio le contesta
// que el material no está en su aparato. Cierto para la máquina, mentira para
// quien lo lee: «Listo» significa «ya puedes estudiarlo».
//
// La regla firmada: **CON señal, un documento servible que todavía no está
// copiado se ve EXACTAMENTE como uno procesando** — mismo chip, misma barra, y
// el 100 y el «Listo» sólo cuando la fila local aterriza. El usuario no tiene
// por qué enterarse de que hay una descarga; lo único que percibe es que
// termina de prepararse.
//
// SIN señal manda el aviso honesto de siempre (`PREPARING`): ahí la descarga NO
// está corriendo, la espera puede ser larga y el usuario merece saber por qué y
// tener su reintento manual. Disfrazarlo de «Procesando» sería dejarlo mirando
// una barra congelada sin explicación y sin salida.

// Estados de entrega de una tarjeta del Panel. Son los ÚNICOS tres; no hay
// vocabulario de máquina nuevo ni estados intermedios inventados.
export const CARD_DELIVERY = {
  // Nada que decir: la tarjeta se pinta exactamente como hoy.
  NORMAL: 'normal',
  // Sin señal + el servidor lo daba por servible + no está copiado al
  // teléfono → aviso honesto en el renglón del estado, tarjeta = reintentar.
  PREPARING: 'preparing',
  // #412 — CON señal + el servidor lo daba por servible + no está copiado
  // todavía → la tarjeta se pinta como PROCESANDO (chip, barra congelada en su
  // último valor honesto y ningún 100). Sin texto propio y sin affordance
  // nueva: es deliberadamente indistinguible de una cocina en curso.
  DOWNLOADING: 'downloading',
};

// Los estados de servidor que representan material SERVIBLE. Espejo del mismo
// criterio que usa la convergencia de F6.7a (convergence.js:41) — se repite
// aquí como literal a propósito: este módulo es puro y sin imports, y un
// acoplamiento a la tubería de F6.7a sería consumo encubierto de su interior.
const SERVABLE = ['ready', 'partial'];

function finiteOr(v, fallback) {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

/**
 * ¿Este doc está copiado al teléfono con material de verdad?
 *
 * "Copiado" no es "hay fila": una fila con las tablas vacías es el desgarro
 * que la fila sola no delata (el mismo caso que el núcleo de convergencia
 * audita). Copiado = hay fila Y alguna de las dos tablas tiene renglones.
 */
export function isCopiedToDevice(presenceRow) {
  if (!presenceRow) return false;
  const q = finiteOr(presenceRow.local_question_rows, 0);
  const f = finiteOr(presenceRow.local_flashcard_rows, 0);
  return q > 0 || f > 0;
}

/**
 * computeCardDelivery — la fila de la tabla que le toca a UNA tarjeta del Panel.
 *
 * @param {{status?: string}} doc         el doc tal como el Panel lo pinta
 * @param {object|null} presenceRow       su fila de readLocalPresence(), o null
 * @param {{offline: boolean, appBuild: boolean, presenceRead: boolean}} ctx
 * @returns {'normal'|'preparing'|'downloading'}
 *
 * REGLAS (en este orden, y no hay más):
 *   - Web: siempre NORMAL. La web no tiene bodega y su Panel es verdad de
 *     servidor pura — render byte-idéntico al de hoy.
 *   - Presencia NO leída todavía: NORMAL. La pantalla espera la lectura antes
 *     de decidir — nunca se pinta un aviso (ni se convierte la tarjeta en
 *     botón de reintento) por no haber preguntado aún. Quién evita el «Listo»
 *     de ese instante es el Panel, con su placeholder (#412, ajuste 1): aquí
 *     no se puede, porque esta función no sabe todavía nada del teléfono.
 *   - Estado último conocido `processing`, o cualquier otro (error, uploaded,
 *     archived): NORMAL — conservan sus flujos actuales intactos.
 *   - Estado servible + copiado: NORMAL.
 *   - Estado servible + NO copiado + SIN señal: PREPARING (el aviso honesto de
 *     siempre, byte por byte).
 *   - Estado servible + NO copiado + CON señal: DOWNLOADING (#412).
 *
 * `offline` es el que la pantalla ya calcula: el dictamen DOWN del vigía O la
 * evidencia de que su propio fetch vivo murió con forma de red
 * (`Dashboard.js`, `offlineData || net.isOffline`). Las dos cosas significan lo
 * mismo aquí —«esta descarga no está corriendo ahora»— y usar ESE valor es lo
 * que mantiene el camino de PREPARING idéntico al de hoy: los dos estados son
 * exhaustivos y mutuamente excluyentes sobre la misma condición.
 */
export function computeCardDelivery(doc, presenceRow, ctx) {
  const { offline = false, appBuild = false, presenceRead = false } = ctx || {};
  if (!appBuild || !presenceRead) return CARD_DELIVERY.NORMAL;
  if (!doc || !SERVABLE.includes(doc.status)) return CARD_DELIVERY.NORMAL;
  if (isCopiedToDevice(presenceRow)) return CARD_DELIVERY.NORMAL;
  return offline ? CARD_DELIVERY.PREPARING : CARD_DELIVERY.DOWNLOADING;
}

// ── Centro de Estudio: disponibilidad POR MODO ──────────────────────────────

// Qué material necesita cada modo. Adaptive y Exam corren sobre preguntas;
// Flashcards sobre flashcards. Un modo desconocido no exige nada (no se
// inventa un requisito para algo que no existe).
export const MODE_MATERIAL = {
  adaptive: 'questions',
  exam: 'questions',
  flashcards: 'flashcards',
};

export function materialForMode(mode) {
  return MODE_MATERIAL[mode] || null;
}

/**
 * docMaterialCounts — cuánto material tiene ESTE doc, de verdad.
 *
 * Con fila de presencia (app): lo que las TABLAS tienen en el teléfono. Es la
 * verdad del teléfono, que es la que manda en el Centro de Estudio.
 *
 * Sin fila de presencia: los conteos del servidor tal cual. Esto es lo que
 * mantiene la web byte-idéntica — allá readLocalPresence() devuelve [] por
 * construcción, así que NUNCA hay fila y el cálculo es el de hoy. En app,
 * un doc listado sin fila cae al mismo camino: la puerta de estudio de F6.5b
 * (POOL_NOT_READY) sigue siendo quien lo detiene, no este módulo.
 */
export function docMaterialCounts(doc, presenceRow) {
  if (presenceRow) {
    return {
      questions: finiteOr(presenceRow.local_question_rows, 0),
      flashcards: finiteOr(presenceRow.local_flashcard_rows, 0),
    };
  }
  return {
    questions: finiteOr(doc && doc.question_count, 0),
    flashcards: finiteOr(doc && doc.flashcard_count, 0),
  };
}

/**
 * modeAvailableForDoc — ¿este doc sirve para este modo?
 * preguntas>0 → Adaptive/Exam; flashcards>0 → Flashcards.
 */
export function modeAvailableForDoc(doc, presenceRow, mode) {
  const needs = materialForMode(mode);
  if (!needs) return true;
  const counts = docMaterialCounts(doc, presenceRow);
  return counts[needs] > 0;
}

// Las dos variantes espejo del mensaje multi-doc. El mensaje se cuelga DEBAJO
// del renglón del doc culpable, por eso no repite su nombre.
export const MULTIDOC_NOTICE = {
  NO_QUESTIONS: 'no_questions',
  NO_FLASHCARDS: 'no_flashcards',
};

/**
 * multiDocNoticeFor — el aviso que le toca al renglón de UN doc, o null.
 *
 * Devuelve la variante espejo según lo que el MODO pide y al doc le falta.
 * Solo opina sobre docs SELECCIONADOS (un doc que nadie eligió no tiene por
 * qué llevar un reclamo colgando).
 */
export function multiDocNoticeFor(doc, presenceRow, mode, selected) {
  if (!selected) return null;
  const needs = materialForMode(mode);
  if (!needs) return null;
  if (modeAvailableForDoc(doc, presenceRow, mode)) return null;
  return needs === 'questions' ? MULTIDOC_NOTICE.NO_QUESTIONS : MULTIDOC_NOTICE.NO_FLASHCARDS;
}

/**
 * selectionBlockedByMaterial — ¿alguno de los seleccionados le falta lo que el
 * modo pide? Es el espejo exacto de multiDocNoticeFor sobre toda la selección:
 * si hay al menos un aviso colgando, la sesión no puede arrancar.
 */
export function selectionBlockedByMaterial(docs, selectedIds, presenceById, mode) {
  const needs = materialForMode(mode);
  if (!needs) return false;
  const ids = Array.isArray(selectedIds) ? selectedIds : [];
  if (ids.length === 0) return false;
  return (Array.isArray(docs) ? docs : [])
    .filter((d) => d && ids.includes(d.id))
    .some((d) => !modeAvailableForDoc(d, presenceById ? presenceById.get(d.id) : null, mode));
}

/** Índice por id de la lista que devuelve readLocalPresence(). */
export function presenceIndex(rows) {
  const m = new Map();
  for (const r of Array.isArray(rows) ? rows : []) {
    if (r && r.id != null) m.set(r.id, r);
  }
  return m;
}
